// PdfPages — a PDF shown ON THE PAGE, every page, on every device.
//
//   Miles, 2026-09-24: "the status page of who has signed should just display
//   the lease rather than making someone click to view."
//
// The bytes arrive already fetched (the caller owns the request and its
// credentials). Each page is rendered with pdf.js (lib/pdfjsLegacy, loaded on
// demand) into an off-screen canvas, converted to an image, and the canvas is
// released. Images instead of live canvases because a 30-page lease at retina
// width is hundreds of megabytes of canvas backing store — past what mobile
// Safari allows a page — while the same pages as PNGs are a few megabytes the
// browser can decode and discard as they scroll by. Pages render lazily as they
// approach the viewport, so opening the page costs one page, not thirty.
//
// Pinch-zoom works natively on the images; the download link beside this
// component remains the way to get the file itself.

import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';

/** Cap on device-pixel-ratio: sharper than 2× buys nothing legible and
 *  doubles memory again. */
const MAX_DPR = 2;
/** Widest bitmap we will render a page at, in device pixels. */
const MAX_RENDER_WIDTH = 1800;
/** Start rendering a page when it is this close to the viewport. */
const PRELOAD_MARGIN = '1200px 0px';

interface PageSize {
  width: number;
  height: number;
}

function PdfPageImage({
  doc,
  pageNumber,
  pageCount,
  size,
  title,
}: {
  doc: PDFDocumentProxy;
  pageNumber: number;
  pageCount: number;
  size: PageSize;
  title: string;
}): React.JSX.Element {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Become visible once, when the page nears the viewport. Without
  // IntersectionObserver (very old engines) every page renders straight away.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: PRELOAD_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    let task: { cancel: () => void } | null = null;
    void (async () => {
      try {
        const page = await doc.getPage(pageNumber);
        if (cancelled) return;
        const cssWidth = wrapRef.current?.clientWidth || 800;
        const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
        const targetWidth = Math.min(Math.round(cssWidth * dpr), MAX_RENDER_WIDTH);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: targetWidth / base.width });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no 2d context');
        // PDF pages are white paper; a transparent page would show the card
        // behind it (and read as black in dark mode).
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const renderTask = page.render({ canvasContext: ctx, viewport });
        task = renderTask;
        await renderTask.promise;
        if (cancelled) return;
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        // Release the backing store now — the image is all we keep.
        canvas.width = 0;
        canvas.height = 0;
        page.cleanup();
        if (cancelled || !blob) {
          if (!blob && !cancelled) setFailed(true);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      try {
        task?.cancel();
      } catch {
        /* already finished */
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, doc, pageNumber]);

  return (
    <div
      ref={wrapRef}
      style={{
        width: '100%',
        aspectRatio: `${size.width} / ${size.height}`,
        background: '#ffffff',
        border: '1px solid var(--border, rgba(0,0,0,0.12))',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <img
          src={src}
          alt={`${title}, page ${pageNumber} of ${pageCount}`}
          style={{ display: 'block', width: '100%', height: '100%' }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b6b6b',
            fontSize: 'var(--text-sm)',
          }}
        >
          {failed ? `Page ${pageNumber} could not be shown.` : `Page ${pageNumber} of ${pageCount}`}
        </div>
      )}
    </div>
  );
}

export function PdfPages({
  data,
  title,
  onError,
}: {
  /** The PDF's bytes. Copied before pdf.js takes them, so the caller's buffer
   *  stays usable (pdf.js transfers what it is given to its worker). */
  data: ArrayBuffer;
  /** Accessible name — "Lease", "Payment Plan". Never an id. */
  title: string;
  /** Called once when the document cannot be read at all (bad bytes, an
   *  engine pdf.js cannot run on). The caller decides what to show instead. */
  onError?: (err: unknown) => void;
}): React.JSX.Element | null {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [sizes, setSizes] = useState<PageSize[] | null>(null);
  const [failed, setFailed] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setDoc(null);
    setSizes(null);
    setFailed(false);
    void (async () => {
      try {
        const { loadPdfjs } = await import('@/lib/pdfjsLegacy');
        const pdfjs = await loadPdfjs();
        const task = pdfjs.getDocument({
          data: new Uint8Array(data.slice(0)),
          // Hardening: never evaluate font programs as JS.
          isEvalSupported: false,
        });
        const pdf = await task.promise;
        loaded = pdf;
        if (cancelled) {
          void pdf.destroy();
          return;
        }
        const pageSizes: PageSize[] = [];
        for (let n = 1; n <= pdf.numPages; n += 1) {
          const page = await pdf.getPage(n);
          const vp = page.getViewport({ scale: 1 });
          pageSizes.push({ width: vp.width, height: vp.height });
        }
        if (cancelled) return;
        setDoc(pdf);
        setSizes(pageSizes);
      } catch (err) {
        if (cancelled) return;
        setFailed(true);
        onErrorRef.current?.(err);
      }
    })();
    return () => {
      cancelled = true;
      if (loaded) void loaded.destroy();
    };
  }, [data]);

  if (failed) return null;
  if (!doc || !sizes) {
    return (
      <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
        Loading the document…
      </p>
    );
  }
  return (
    <div
      role="document"
      aria-label={title}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
    >
      {sizes.map((size, i) => (
        <PdfPageImage
          key={i + 1}
          doc={doc}
          pageNumber={i + 1}
          pageCount={sizes.length}
          size={size}
          title={title}
        />
      ))}
    </div>
  );
}
